import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { methodologyService, Methodology, SubMethodology, auth, clearUserSessionData } from "../../lib/firebase";
import { useSystemSettings } from "../../lib/SystemSettingsContext";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import PageWrapper from "../../components/PageWrapper";

interface MethodologyParams {
  methodologyId: string;
}

// Helper function to count key points across all content entries of a sub-methodology
const countTotalKeyPoints = async (methodologyId: string, subMethodologyId: string): Promise<number> => {
  try {
    const entries = await methodologyService.getAllSubMethodologyEntries(methodologyId, subMethodologyId);
    
    // Count all key points in all entries
    let totalKeyPoints = 0;
    entries.forEach(entry => {
      if (entry.keyPoints && Array.isArray(entry.keyPoints)) {
        totalKeyPoints += entry.keyPoints.length;
      }
    });
    
    return totalKeyPoints;
  } catch (error) {
    console.error("Error counting key points:", error);
    return 0;
  }
};

export const StraightUpMethodology = (): JSX.Element => {
  const { methodologyId = "straight-up" } = useParams<keyof MethodologyParams>() as MethodologyParams;
  const location = useLocation();
  const { settings } = useSystemSettings();
  
  // Check if this is a new session from the URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const isNewSession = searchParams.get('newSession') === 'true';
  
  // Add debug logging for the newSession parameter
  console.log(`StraightUpMethodology - isNewSession: ${isNewSession}, search: ${location.search}`);
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userName, setUserName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [methodology, setMethodology] = useState<Methodology | null>(null);
  const [subMethodologyKeyPoints, setSubMethodologyKeyPoints] = useState<Record<string, number>>({});
  const navigate = useNavigate();
  const auth = getAuth();

  // Check if this is the Pathway methodology and if it's disabled
  useEffect(() => {
    if (methodologyId === "pathway" && settings.disablePathway) {
      setError("The Pathway methodology is currently disabled by the administrator.");
      navigate('/dashboard');
    }
  }, [methodologyId, settings.disablePathway, navigate]);

  // Authentication effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const displayName = user.displayName || user.email?.split('@')[0] || 'User';
        setUserName(displayName);
        localStorage.setItem('rawpa_user_name', displayName);
      } else {
        navigate('/signin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Function to navigate with preserving newSession parameter
  const navigateWithSessionParam = (path: string) => {
    if (isNewSession) {
      navigate(`${path}?newSession=true`);
    } else {
      navigate(path);
    }
  };

  // Load methodology
  useEffect(() => {
    console.group('MethodologyPage - Loading Data');
    console.log(`Loading methodology: ${methodologyId}`);
    setLoading(true);

    // Subscribe to the methodology in Firestore
    const unsubscribe = methodologyService.subscribeToMethodology(methodologyId, async (methodologyData) => {
      console.log("Received methodology update:", methodologyData);
      if (methodologyData) {
        setMethodology(methodologyData);
        console.log(`Methodology has ${methodologyData.subMethodologies.length} sub-methodologies:`, 
          methodologyData.subMethodologies.map(sm => sm.title).join(', '));
        
        // For each sub-methodology, count total key points
        if (methodologyData.subMethodologies && methodologyData.subMethodologies.length > 0) {
          const keyPointsData: Record<string, number> = {};
          
          // Create an array of promises to fetch key points counts in parallel
          const countPromises = methodologyData.subMethodologies.map(async (subMethodology) => {
            const count = await countTotalKeyPoints(methodologyId, subMethodology.id);
            return { id: subMethodology.id, count };
          });
          
          // Wait for all counts to be fetched
          const results = await Promise.all(countPromises);
          
          // Build the results object
          results.forEach(result => {
            keyPointsData[result.id] = result.count;
          });
          
          setSubMethodologyKeyPoints(keyPointsData);
        }
      } else {
        console.error("Failed to load methodology");
        setError("Failed to load methodology data");
      }
      setLoading(false);
    });

    return () => {
      console.log("Cleaning up methodology subscription");
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      console.groupEnd();
    };
  }, [methodologyId]);

  const handleSignOut = async () => {
    try {
      // Clear all user session data
      const userId = auth.currentUser?.uid;
      await clearUserSessionData(userId);
      
      // Sign out from Firebase
      await auth.signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <PageWrapper 
      title={methodology?.title || "Methodology"}
      preserveNewSession={true}
      showBackToDashboard={true}
    >
      {error && (
        <Alert className="mb-4 bg-yellow-50 border-yellow-200">
          <AlertTitle className="text-yellow-800">Error</AlertTitle>
          <AlertDescription className="text-yellow-700">
            {error}
          </AlertDescription>
        </Alert>
      )}
      
      {methodologyId === "pathway" && settings.disablePathway ? (
        <Alert className="mb-4 bg-yellow-50 border-yellow-200">
          <AlertTitle className="text-yellow-800">Pathway Methodology Unavailable</AlertTitle>
          <AlertDescription className="text-yellow-700">
            The Pathway methodology is currently disabled by the administrator.
          </AlertDescription>
        </Alert>
      ) : (
        <div>
          <p className="font-['Space_Grotesk'] text-sm text-black opacity-80 mb-6">
            {methodology?.description || "Select a methodology to begin your security assessment"}
          </p>
          
          {loading ? (
            <div className="text-center py-8">
              <p>Loading methodology items...</p>
            </div>
          ) : methodology?.subMethodologies && methodology.subMethodologies.length > 0 ? (
            <div className="space-y-4">
              {methodology.subMethodologies.map((item, index) => (
                <motion.button
                  key={item.id}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ 
                    duration: 0.3,
                    delay: index * 0.1,
                    ease: "easeOut"
                  }}
                  onClick={() => navigateWithSessionParam(`/methodology/${methodologyId}/${item.id}`)}
                  className="w-full h-[100px] bg-white rounded-[20px] flex items-center justify-between px-6 shadow-md hover:bg-gray-50 transition-colors border border-[#E7E7E7] group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#daed6e] rounded-full flex items-center justify-center">
                      <img src="/security-icon.svg" alt={item.title} className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black">
                        {item.title}
                      </h3>
                      <p className="font-['Space_Grotesk'] text-sm text-black opacity-60">
                        {item.description}
                      </p>
                      <div className="mt-1 flex gap-1 flex-wrap">
                        {/* Use the combined count from all entries if available, otherwise fall back to detailedContent */}
                        {subMethodologyKeyPoints[item.id] > 0 ? (
                          <span className="inline-block px-2 py-0.5 bg-[#daed6e40] text-black text-xs rounded">
                            {subMethodologyKeyPoints[item.id]} key points
                          </span>
                        ) : item.detailedContent?.keyPoints && item.detailedContent.keyPoints.length > 0 ? (
                          <span className="inline-block px-2 py-0.5 bg-[#daed6e40] text-black text-xs rounded">
                            {item.detailedContent.keyPoints.length} key points
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-2xl">→</span>
                  </div>
                </motion.button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No sub-methodologies available. Please contact an administrator.
            </div>
          )}
        </div>
      )}
    </PageWrapper>
  );
}; 